import { useContext, useState } from 'react';
import InputField from 'components/common/input-field';
import PrimaryButtom from 'components/common/buttons/primary-btn';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as TickIcon } from 'assets/icon/primary-tick.svg';
import { useAppDispatch } from 'hooks/useAppDispatchSelector';
import { passRegEx } from 'helpers/utils';
import { resetPassword } from 'store/features/authSlice';
import toast from 'react-hot-toast';
import { AppContext } from 'context/app-context';
import { ReactComponent as ExclaimationIcon } from 'assets/login/exclamation-circle.svg';
import { IResetData, ResetStepType } from 'interfaces/auth-pages-interfaces';
import { useTranslation } from 'react-i18next';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);
  const { token } = useParams();
  const [resetData, setResetData] = useState<IResetData>({ password: '', confirmPassword: '' });
  const [step, setStep] = useState<ResetStepType>('RESET');
  const [error, setError] = useState<Pick<IResetData, 'password'>>({ password: '' });
  const dispatch = useAppDispatch();

  const isValid = () => {
    const { password, confirmPassword } = resetData;
    if (!passRegEx.test(password) || password !== confirmPassword) {
      setError({
        password: !passRegEx.test(password)
          ? t('RESET_PASSWORD_ERROR_MESSAGE_1')
          : password !== confirmPassword
          ? t('RESET_PASSWORD_ERROR_MESSAGE_2')
          : '',
      });
      return false;
    } else {
      setError({ ...error, password: '' });
      return true;
    }
  };
  const handleChange = (field: string, value: string) => {
    setResetData({ ...resetData, [field]: value });
  };
  const handleResetPassword = () => {
    if (!isValid()) {
      return;
    }
    setLoader(true);
    const { password } = resetData;
    dispatch(
      resetPassword({
        request: {
          token: token,
          password: password,
        },
      }),
    )
      .then(() => {
        setLoader(false);
        toast.success(t('RESET_PASSWORD_SUCCESS_TOAST'));
        setStep('SUCCESS');
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const step1 = (
    <div className="flex flex-col items-center w-full gap-y-4">
      <p className="font-medium text-3xl text-center w-full">{t('RESET_PASSWORD')}</p>
      <p className="text-base text-center text-grey2 w-full font-regular">
        {t('RESET_PASS_SUBTITLE')}
      </p>
      <div className="flex flex-col w-full gap-y-2">
        <p className="text-sm w-full">{t('PASSWORD')}</p>
        <InputField
          value={resetData?.password || ''}
          onChange={(data: string) => handleChange('password', data)}
          placeholder={t('ENTER_PASSWORD')}
          error={error?.password || ''}
          disabled={false}
          type="password"
        />
      </div>
      <div className="flex flex-wrap">
        <div className="flex flex-wrap items-center w-full gap-x-2">
          <ExclaimationIcon height={20} width={20} />
          <label className="text-sm font-regular text-grey9 flex-1 break-words">
            {t('PASS_VALIDATION_1')}
          </label>
        </div>
        <div className="flex items-center w-full gap-x-2 mt-2">
          <ExclaimationIcon height={20} width={20} />
          <label className="text-sm font-regular text-grey9 flex-1 break-words">
            {t('PASS_VALIDATION_2')}
          </label>
        </div>
      </div>
      <div className="flex flex-col w-full gap-y-2">
        <p className="text-sm w-full">{t('CONFIRM_PASSWORD')}</p>
        <InputField
          value={resetData?.confirmPassword || ''}
          onChange={(data: string) => handleChange('confirmPassword', data)}
          placeholder={t('ENTER_PASSWORD')}
          disabled={false}
          type="password"
        />
      </div>
      <div className="flex items-center justify-center mt-8">
        <PrimaryButtom
          label={t('RESET_PASSWORD')}
          onClick={() => handleResetPassword()}
          disabled={!resetData?.password.trim() || !resetData?.confirmPassword?.trim()}
        />
      </div>
    </div>
  );
  const step2 = (
    <div className="flex flex-col items-center w-full gap-y-6">
      <TickIcon />
      <p className="font-bold text-6xl text-center w-full text-primary">{t('SUCCESS')}</p>
      <p className="text-base text-center w-full font-regular">{t('RESET_PASS_SUCCESS_MESSAGE')}</p>
      <div className="flex items-center justify-center mt-8">
        <PrimaryButtom label={t('BACK_TO_HOME')} onClick={() => navigate('/login')} />
      </div>
    </div>
  );
  return step === 'RESET' ? step1 : step2;
};

export default ResetPassword;
